import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { pagamentiApi } from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { 
  ArrowDownCircle,
  ArrowUpCircle,
  Clock,
  FileCheck,
  CheckCircle2,
  Download,
  RefreshCw,
  CreditCard,
  Banknote,
  Mail,
  AlertTriangle,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  Upload,
  Trash2,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const PagamentiPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('sdd'); // sdd or sepa
  const [selectedStatus, setSelectedStatus] = useState('pending'); // pending, open, completed
  const [selectedPayments, setSelectedPayments] = useState([]); // IDs selezionati
  const [showDataIncassoDialog, setShowDataIncassoDialog] = useState(false);
  const [dataIncasso, setDataIncasso] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({}); // Mesi espansi/collassati
  const [showFatturaDialog, setShowFatturaDialog] = useState(false);
  const [dataFattura, setDataFattura] = useState('');
  const [pendingFatturaAction, setPendingFatturaAction] = useState(null);
  // Verifica Coerenza (riconciliazione pagamenti pending vs contratto)
  const [showCoerenzaDialog, setShowCoerenzaDialog] = useState(false);
  const [coerenzaLoading, setCoerenzaLoading] = useState(false);
  const [coerenzaData, setCoerenzaData] = useState(null);
  const [coerenzaApplying, setCoerenzaApplying] = useState(false);
  const [coerenzaLastVerified, setCoerenzaLastVerified] = useState(() => localStorage.getItem('coerenza_last_verified') || null);
  const [uploadingFattura, setUploadingFattura] = useState(null);
  const fileInputRef = React.useRef(null);

  // Soglia minima per visualizzazione pagamenti (marzo 2026)
  const MIN_DISPLAY_MONTH = '2026-03';

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await pagamentiApi.getAll({
        tipo: selectedType,
        payment_status: selectedStatus
      });
      setPayments(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento pagamenti');
    } finally {
      setLoading(false);
    }
  }, [selectedType, selectedStatus]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Reset selection e espandi primo mese quando cambiano i filtri
  useEffect(() => {
    setSelectedPayments([]);
  }, [selectedType, selectedStatus]);

  // Toggle espansione mese
  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  // Espandi/collassa tutti i mesi
  const toggleAllMonths = (expand) => {
    const newState = {};
    sortedMonths.forEach(month => {
      newState[month] = expand;
    });
    setExpandedMonths(newState);
  };

  // Handle select/deselect single payment
  const handleSelectPayment = (paymentId, checked) => {
    if (checked) {
      setSelectedPayments(prev => [...prev, paymentId]);
    } else {
      setSelectedPayments(prev => prev.filter(id => id !== paymentId));
    }
  };

  // Handle select all payments in current view
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = payments.map(p => p.id);
      setSelectedPayments(allIds);
    } else {
      setSelectedPayments([]);
    }
  };

  // Upload fattura manuale
  const handleUploadFattura = (paymentId) => {
    setUploadingFattura(paymentId);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFattura) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/payments/${uploadingFattura}/fattura-manuale`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Fattura "${file.name}" caricata`);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore upload fattura');
    } finally {
      setUploadingFattura(null);
      e.target.value = '';
    }
  };

  const [importingMonth, setImportingMonth] = useState(null);
  const handleImportaFatturePdfMese = async (month) => {
    setImportingMonth(month);
    try {
      const r = await axios.post(`${API_URL}/payments/importa-fatture-fic-massivo`, { month, tipo: selectedType });
      toast.success(`Import PDF ${selectedType.toUpperCase()} ${month}: ${r.data.importate} importate, ${r.data.saltate_non_inviate} non inviate, ${r.data.errori} errori`);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore import massivo');
    } finally {
      setImportingMonth(null);
    }
  };

  const handleImportaFatturaFic = async (paymentId) => {
    try {
      const r = await axios.post(`${API_URL}/payments/${paymentId}/importa-fattura-fic`);
      toast.success(`Fattura importata da FIC${r.data.ei_status ? ' — SDI: ' + r.data.ei_status : ''}`);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore importazione da FIC');
    }
  };


  const handleDownloadFatturaManuale = async (paymentId) => {
    try {
      const res = await axios.get(`${API_URL}/payments/${paymentId}/fattura-manuale/download`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'];
      const filename = disposition?.match(/filename=(.+)/)?.[1] || `fattura_${paymentId}.pdf`;
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download fattura');
    }
  };

  const handleDeleteFatturaManuale = async (paymentId) => {
    if (!window.confirm('Eliminare la fattura manuale allegata?')) return;
    try {
      await axios.delete(`${API_URL}/payments/${paymentId}/fattura-manuale`);
      toast.success('Fattura eliminata');
      fetchPayments();
    } catch (error) {
      toast.error('Errore eliminazione fattura');
    }
  };

  const handleGenerateDistinta = () => {
    if (selectedPayments.length === 0) {
      toast.error('Seleziona almeno un pagamento');
      return;
    }
    // Imposta data di default a 5 giorni lavorativi da oggi
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 5);
    setDataIncasso(defaultDate.toISOString().split('T')[0]);
    setShowDataIncassoDialog(true);
  };

  // Stato per errori validazione distinta
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrorsDialog, setShowValidationErrorsDialog] = useState(false);

  const handleConfirmGenerateDistinta = async () => {
    try {
      if (!dataIncasso) {
        toast.error('Seleziona la data di incasso');
        return;
      }

      // Trova il mese dal primo pagamento selezionato
      const selectedPaymentData = payments.find(p => p.id === selectedPayments[0]);
      const month = selectedPaymentData?.month?.substring(0, 7) || new Date().toISOString().slice(0, 7);
      
      toast.info(`Generazione XML per ${selectedPayments.length} pagamenti...`);
      const response = await pagamentiApi.generateXML({
        payment_ids: selectedPayments,  // SOLO i selezionati
        month,
        tipo: selectedType,
        data_esecuzione: dataIncasso
      });

      // Scarica il file
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedType.toUpperCase()}_${month}_${selectedPayments.length}pag_${Date.now()}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Distinta generata per ${selectedPayments.length} pagamenti! Ora sono "Aperti"`);
      setSelectedPayments([]);
      setShowDataIncassoDialog(false);
      fetchPayments();
    } catch (error) {
      console.error('Errore generazione distinta:', error);
      
      // Gestisci errori di validazione (dati mancanti)
      // NOTA: La richiesta usa responseType: 'blob', quindi gli errori arrivano come Blob
      let errorData = null;
      
      if (error.response?.data instanceof Blob) {
        // Converti Blob in testo e poi in JSON
        try {
          const text = await error.response.data.text();
          errorData = JSON.parse(text);
        } catch (parseError) {
          console.error('Errore parsing blob:', parseError);
        }
      } else if (error.response?.data) {
        errorData = error.response.data;
      }
      
      if (errorData?.detail) {
        const detail = errorData.detail;
        
        // Se detail è un oggetto con "errors" array (nuovo formato backend)
        if (typeof detail === 'object' && detail.errors && Array.isArray(detail.errors)) {
          setValidationErrors(detail.errors);
          setShowValidationErrorsDialog(true);
          setShowDataIncassoDialog(false);
        }
        // Se è una stringa con più errori separati da newline o è un array
        else if (typeof detail === 'string' && detail.includes('Dati mancanti:')) {
          // Estrai lista errori
          const errorList = detail.replace('Dati mancanti: ', '').split('; ').filter(e => e.trim());
          setValidationErrors(errorList);
          setShowValidationErrorsDialog(true);
          setShowDataIncassoDialog(false);
        } else if (Array.isArray(detail)) {
          setValidationErrors(detail);
          setShowValidationErrorsDialog(true);
          setShowDataIncassoDialog(false);
        } else if (typeof detail === 'string') {
          toast.error(detail);
          setShowDataIncassoDialog(false);
        } else {
          toast.error(detail?.message || 'Errore durante la generazione della distinta');
          setShowDataIncassoDialog(false);
        }
      } else {
        toast.error('Errore durante la generazione della distinta');
        setShowDataIncassoDialog(false);
      }
    }
  };

  const handleBulkComplete = async () => {
    if (selectedPayments.length === 0) {
      toast.error('Seleziona almeno un pagamento');
      return;
    }
    // Apri dialog per chiedere data fattura
    setDataFattura(new Date().toLocaleDateString('en-CA'));
    setPendingFatturaAction({ type: 'bulk', paymentIds: selectedPayments });
    setShowFatturaDialog(true);
  };

  // ===== Fatturazione collettiva sequenziale: avanzamento con polling =====
  const [fatturazioneJob, setFatturazioneJob] = useState(null);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [aggiornandoSdi, setAggiornandoSdi] = useState(false);
  const pollRef = useRef(null);

  const handleAggiornaStatiSdi = async () => {
    setAggiornandoSdi(true);
    try {
      const res = await pagamentiApi.aggiornaStatiSdi({});
      const n = res.data?.da_controllare || 0;
      if (n === 0) {
        toast.success('Stati SDI già allineati, niente da aggiornare');
        setAggiornandoSdi(false);
        return;
      }
      toast.info(`Aggiornamento stati SDI avviato per ${n} fatture… ricarico tra poco`);
      const attesa = Math.min(90000, Math.max(6000, n * 900));
      setTimeout(async () => {
        await fetchPayments();
        setAggiornandoSdi(false);
        toast.success('Stati SDI aggiornati');
      }, attesa);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento stati SDI');
      setAggiornandoSdi(false);
    }
  };

  const startPollingFatturazione = (jobId, totale) => {
    setFatturazioneJob({ status: 'running', totale, elaborate: 0, emesse: 0, saltate: 0, errori: 0, corrente: 0, corrente_label: null, dettagli: [] });
    setShowProgressDialog(true);
    const poll = async () => {
      try {
        const r = await pagamentiApi.statoFatturazioneMassiva(jobId);
        setFatturazioneJob(r.data);
        if (r.data.status === 'completed' || r.data.status === 'error') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          fetchPayments();
          if (r.data.status === 'completed') {
            toast.success(`Fatturazione completata: ${r.data.emesse} emesse, ${r.data.saltate} saltate, ${r.data.errori} errori`);
          } else {
            toast.error('Fatturazione interrotta da un errore');
          }
        }
      } catch (e) {
        // ignora errori transitori di polling
      }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);


  // Conferma pagamento via SDD
  const handleConfermaSDD = async (paymentId) => {
    setDataFattura(new Date().toLocaleDateString('en-CA'));
    setPendingFatturaAction({ type: 'single', paymentId, metodo: 'sdd' });
    setShowFatturaDialog(true);
  };

  // Conferma pagamento via Bonifico
  const handleConfermaBonifico = async (paymentId) => {
    setDataFattura(new Date().toLocaleDateString('en-CA'));
    setPendingFatturaAction({ type: 'single', paymentId, metodo: 'bonifico' });
    setShowFatturaDialog(true);
  };

  // Esegui conferma con fattura
  const handleConfermaConFattura = async (conFattura) => {
    // Validazione data fattura: max oggi, min oggi - 10 giorni
    if (conFattura && dataFattura) {
      // Parso la data come locale (evita lo shift UTC di new Date("YYYY-MM-DD"),
      // che interpreta la stringa come mezzanotte UTC e falsava il confronto)
      const [y, mo, d] = dataFattura.split('-').map(Number);
      const dt = new Date(y, mo - 1, d);
      const oggi = new Date();
      oggi.setHours(0, 0, 0, 0);
      const minDate = new Date(oggi);
      minDate.setDate(minDate.getDate() - 10);
      
      if (dt > oggi) {
        toast.error('La data fattura non può essere successiva a oggi');
        return;
      }
      if (dt < minDate) {
        toast.error('La data fattura non può essere antecedente a 10 giorni');
        return;
      }
    }
    
    setShowFatturaDialog(false);
    const action = pendingFatturaAction;
    if (!action) return;

    try {
      if (action.type === 'single') {
        await pagamentiApi.confermaIncasso({
          payment_id: action.paymentId,
          metodo: action.metodo,
          note: `Confermato ${action.metodo === 'sdd' ? 'addebito SDD' : 'bonifico manuale'}`,
          data_fattura: conFattura ? dataFattura : null,
        });
        const msg = conFattura 
          ? `Pagamento confermato e fattura generata` 
          : `Pagamento confermato via ${action.metodo}`;
        toast.success(msg);
      } else if (action.type === 'bulk') {
        if (conFattura) {
          // Fatturazione collettiva SEQUENZIALE con avanzamento (una alla volta + verifica FiC)
          const res = await pagamentiApi.startFatturazioneMassiva({
            payment_ids: action.paymentIds,
            data_fattura: dataFattura,
          });
          setSelectedPayments([]);
          setPendingFatturaAction(null);
          startPollingFatturazione(res.data.job_id, res.data.totale);
          return;
        }
        const res = await pagamentiApi.bulkUpdateStatus({
          payment_ids: action.paymentIds,
          new_status: 'completed',
          data_fattura: null,
        });
        const fatture = res.data?.fatture;
        if (fatture && conFattura) {
          toast.success(`${action.paymentIds.length} pagamenti completati, ${fatture.successi} fatture generate`);
        } else {
          toast.success(`${action.paymentIds.length} pagamenti completati`);
        }
        setSelectedPayments([]);
      }
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la conferma');
    }
    setPendingFatturaAction(null);
  };

  // Invia sollecito
  const handleSollecita = async (paymentId) => {
    try {
      const response = await pagamentiApi.sollecita({
        payment_id: paymentId,
        messaggio: 'Sollecito pagamento'
      });
      toast.success(`Sollecito inviato (totale: ${response.data.solleciti_totali})`);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio sollecito');
    }
  };

  // Dialog Avviso Pagamento
  const [showAvvisoDialog, setShowAvvisoDialog] = useState(false);
  const [avvisoPayment, setAvvisoPayment] = useState(null);
  const [avvisoOggetto, setAvvisoOggetto] = useState('');
  const [avvisoCorpo, setAvvisoCorpo] = useState('');

  // Apri dialog Avviso Pagamento per rata 1 (bonifico)
  const handleInviaAvvisoPagamento = async (payment) => {
    try {
      // Fetch template
      const tplRes = await axios.get(`${API_URL}/templates/avviso_pagamento_bonifico`);
      const tpl = tplRes.data;
      
      // Recupera i dati del pagamento dall'endpoint avviso
      let avvisoData = {};
      try {
        const avvisoRes = await axios.post(`${API_URL}/payments/${payment.id}/avviso-pagamento`, {});
        avvisoData = avvisoRes.data?.avviso || {};
      } catch {
        avvisoData = {
          pagatore_nome: payment.pagatore_nome || '',
          codice_contratto: payment.contract_code || '',
          rata: `${payment.rata_numero || ''}/${payment.rata_totale || ''}`,
          importo: payment.sdd_amount || 0,
          mese_riferimento: payment.month || '',
          iban_destinatario: '',
          causale: payment.categoria === 'straordinario' && payment.straordinario_descrizione
            ? payment.straordinario_descrizione
            : `Canone locazione ${payment.contract_code} - Mensilità ${payment.rata_numero}`
        };
      }

      // Sostituisci variabili nel template
      let oggetto = tpl.oggetto || '';
      let corpo = tpl.corpo || '';
      for (const [key, value] of Object.entries(avvisoData)) {
        const placeholder = `{${key}}`;
        oggetto = oggetto.replaceAll(placeholder, String(value));
        corpo = corpo.replaceAll(placeholder, String(value));
      }

      setAvvisoPayment(payment);
      setAvvisoOggetto(oggetto);
      setAvvisoCorpo(corpo);
      setShowAvvisoDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento del template avviso');
    }
  };

  const handleConfirmAvviso = async () => {
    toast.success(`Avviso di pagamento preparato per ${avvisoPayment?.pagatore_nome || 'conduttore'}`);
    toast.info('Integrazione email SMTP non ancora attiva - il testo è pronto per invio manuale');
    setShowAvvisoDialog(false);
    fetchPayments();
  };

  // Sposta pagamenti selezionati in "Aperti"
  const handleMoveToOpen = async () => {
    if (selectedPayments.length === 0) {
      toast.error('Seleziona almeno un pagamento');
      return;
    }
    try {
      await pagamentiApi.bulkUpdateStatus({
        payment_ids: selectedPayments,
        new_status: 'open'
      });
      toast.success(`${selectedPayments.length} pagamenti spostati in Aperti`);
      setSelectedPayments([]);
      fetchPayments();
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  // Sposta pagamenti selezionati in "In Attesa"
  const handleMoveToPending = async () => {
    if (selectedPayments.length === 0) {
      toast.error('Seleziona almeno un pagamento');
      return;
    }
    try {
      await pagamentiApi.bulkUpdateStatus({
        payment_ids: selectedPayments,
        new_status: 'pending'
      });
      toast.success(`${selectedPayments.length} pagamenti spostati in Attesa`);
      setSelectedPayments([]);
      fetchPayments();
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  // Genera XML solo per i pagamenti selezionati
  const handleGenerateXMLForSelected = async () => {
    if (selectedPayments.length === 0) {
      toast.error('Seleziona almeno un pagamento');
      return;
    }
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await pagamentiApi.generateXML({
        payment_ids: selectedPayments,
        tipo: selectedType,
        month: currentMonth
      });
      
      // Scarica il file XML
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedType.toUpperCase()}_${currentMonth}_${Date.now()}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`XML generato per ${selectedPayments.length} pagamenti. I pagamenti sono stati spostati in "Aperti".`);
      setSelectedPayments([]);
      fetchPayments();
    } catch (error) {
      // Gestione errori - il responseType è blob quindi dobbiamo convertire
      let errorData = null;
      
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          errorData = JSON.parse(text);
        } catch (parseError) {
          console.error('Errore parsing blob:', parseError);
        }
      } else if (error.response?.data) {
        errorData = error.response.data;
      }
      
      const detail = errorData?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        // Mostra errori nel dialog dedicato
        setValidationErrors(detail.errors);
        setShowValidationErrorsDialog(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Errore durante la generazione XML');
      }
    }
  };

  const handleGenerateMonthlyPayments = async () => {
    try {
      // Genera pagamenti per il mese corrente E il mese successivo
      // (i pagamenti vanno preparati il mese prima)
      const currentMonth = new Date();
      const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
      
      const currentMonthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
      const nextMonthStr = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`;
      
      toast.info(`Aggiornamento pagamenti per ${currentMonthStr} e ${nextMonthStr}...`);
      
      // Genera per mese corrente
      const response1 = await pagamentiApi.generateMonthly(currentMonthStr);
      // Genera per mese successivo
      const response2 = await pagamentiApi.generateMonthly(nextMonthStr);
      
      const totalCreated = (response1.data.payments_created || 0) + (response2.data.payments_created || 0);
      const totalChecked = (response1.data.contracts_checked || 0) + (response2.data.contracts_checked || 0);
      
      if (totalCreated > 0) {
        toast.success(
          `Verificati ${totalChecked} contratti. Creati ${totalCreated} nuovi pagamenti per ${currentMonthStr} e ${nextMonthStr}`
        );
      } else {
        toast.info('Tutti i pagamenti sono già presenti per questo mese e il prossimo');
      }
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    }
  };

  // Verifica Coerenza: rileva/corregge disallineamenti pending vs dati contratto
  const handleVerificaCoerenza = async () => {
    setShowCoerenzaDialog(true);
    setCoerenzaLoading(true);
    setCoerenzaData(null);
    try {
      const resp = await axios.post(
        `${API_URL}/contracts/reconcile-pending-payments?dry_run=true`,
        {},
        { withCredentials: true }
      );
      setCoerenzaData(resp.data);
      const ts = new Date().toISOString();
      setCoerenzaLastVerified(ts);
      localStorage.setItem('coerenza_last_verified', ts);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore verifica coerenza');
      setShowCoerenzaDialog(false);
    } finally {
      setCoerenzaLoading(false);
    }
  };

  const handleApplicaCorrezioni = async () => {
    setCoerenzaApplying(true);
    try {
      const resp = await axios.post(
        `${API_URL}/contracts/reconcile-pending-payments?dry_run=false`,
        {},
        { withCredentials: true }
      );
      toast.success(`Correzioni applicate: ${resp.data.aggiornati} pagamenti riallineati`);
      setCoerenzaData(resp.data);
      const ts = new Date().toISOString();
      setCoerenzaLastVerified(ts);
      localStorage.setItem('coerenza_last_verified', ts);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore applicazione correzioni');
    } finally {
      setCoerenzaApplying(false);
    }
  };


  // Export pagamenti XLSX/PDF
  const handleExport = async (format, month = null) => {
    try {
      const params = { tipo: selectedType, payment_status: selectedStatus };
      if (month) params.month = month;
      
      toast.info(`Generazione ${format.toUpperCase()} in corso...`);
      const response = await pagamentiApi.exportData(format, params);
      
      const blob = new Blob([response.data], {
        type: format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthLabel = month || 'tutti';
      a.download = `pagamenti_${selectedType}_${monthLabel}_${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Export ${format.toUpperCase()} scaricato`);
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };


  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      open: { label: 'Aperto', color: 'bg-blue-100 text-blue-800', icon: FileCheck },
      completed: { label: 'Concluso', color: 'bg-green-100 text-green-800', icon: CheckCircle2 }
    };

    const config = statusMap[status] || statusMap.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatMonth = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { year: 'numeric', month: 'long' });
  };

  // Raggruppa pagamenti per mese
  const paymentsByMonth = payments.reduce((acc, payment) => {
    const month = payment.month.substring(0, 7);
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(payment);
    return acc;
  }, {});

  const sortedMonths = Object.keys(paymentsByMonth).sort().reverse();
  
  // Inizializza mesi espansi: il primo mese è sempre aperto di default
  useEffect(() => {
    if (sortedMonths.length > 0 && Object.keys(expandedMonths).length === 0) {
      setExpandedMonths({ [sortedMonths[0]]: true });
    }
  }, [sortedMonths, expandedMonths]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Pagamenti</h1>
          <p className="text-sm text-slate-600 mt-1">
            Gestisci addebiti SDD e bonifici SEPA con workflow tri-stato
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => handleExport('xlsx')}
            disabled={payments.length === 0}
            data-testid="export-xlsx-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            XLSX
          </Button>
          <Button 
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={payments.length === 0}
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <div className="flex flex-col items-start">
            <Button
              variant="outline"
              onClick={handleVerificaCoerenza}
              data-testid="verifica-coerenza-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Verifica Coerenza
            </Button>
            {coerenzaLastVerified && (
              <span className="text-[11px] text-slate-400 mt-1 whitespace-nowrap" data-testid="coerenza-last-verified">
                Ultima verifica: {new Date(coerenzaLastVerified).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <Button 
            onClick={handleGenerateMonthlyPayments}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="generate-monthly-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna Pagamenti Mese
          </Button>
          <Button
            onClick={handleAggiornaStatiSdi}
            variant="outline"
            disabled={aggiornandoSdi}
            title="Rilegge da Fatture in Cloud lo stato SDI reale delle fatture (es. da 'processing' a 'sent')"
            data-testid="refresh-sdi-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${aggiornandoSdi ? 'animate-spin' : ''}`} />
            {aggiornandoSdi ? 'Aggiornamento…' : 'Aggiorna stati SDI'}
          </Button>
        </div>
      </div>

      {/* Type Selector: SDD vs SEPA */}
      <div className="grid grid-cols-2 gap-4">
        <Card
          className={`cursor-pointer transition-all ${
            selectedType === 'sdd'
              ? 'ring-2 ring-green-500 bg-green-50'
              : 'hover:bg-slate-50'
          }`}
          onClick={() => setSelectedType('sdd')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <ArrowDownCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">SDD - In Entrata</h3>
                <p className="text-sm text-slate-600">Addebiti da conduttori</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            selectedType === 'sepa'
              ? 'ring-2 ring-blue-500 bg-blue-50'
              : 'hover:bg-slate-50'
          }`}
          onClick={() => setSelectedType('sepa')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <ArrowUpCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">SEPA - In Uscita</h3>
                <p className="text-sm text-slate-600">Bonifici a proprietari</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="pending" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  In Attesa
                </TabsTrigger>
                <TabsTrigger value="open" className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4" />
                  Aperti
                </TabsTrigger>
                <TabsTrigger value="completed" className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Conclusi
                </TabsTrigger>
              </TabsList>

              {/* Action Buttons */}
              <div className="flex gap-2 flex-wrap">
                {selectedPayments.length > 0 && (
                  <Badge variant="secondary" className="mr-2">
                    {selectedPayments.length} selezionati
                  </Badge>
                )}
                
                {/* Bottoni per "In Attesa" */}
                {selectedStatus === 'pending' && selectedPayments.length > 0 && (
                  <>
                    <Button onClick={handleGenerateDistinta} data-testid="generate-xml-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Genera Distinta XML ({selectedPayments.length})
                    </Button>
                    <Button onClick={handleMoveToOpen} variant="outline">
                      <FileCheck className="w-4 h-4 mr-2" />
                      Sposta in Aperti
                    </Button>
                  </>
                )}
                
                {/* Bottoni per "Aperti" */}
                {selectedStatus === 'open' && selectedPayments.length > 0 && (
                  <>
                    <Button onClick={handleBulkComplete} variant="success" data-testid="confirm-payments-btn">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {selectedType === 'sepa' ? 'Conferma Pagamenti' : 'Conferma Incassi'} ({selectedPayments.length})
                    </Button>
                    <Button onClick={handleMoveToPending} variant="outline">
                      <Clock className="w-4 h-4 mr-2" />
                      Riporta in Attesa
                    </Button>
                  </>
                )}
                
                {/* Bottoni per "Conclusi" */}
                {selectedStatus === 'completed' && selectedPayments.length > 0 && (
                  <Button onClick={handleMoveToOpen} variant="outline">
                    <FileCheck className="w-4 h-4 mr-2" />
                    Riporta in Aperti
                  </Button>
                )}
              </div>
            </div>

            {/* Content for all tabs */}
            <TabsContent value={selectedStatus} className="mt-0">
              {loading ? (
                <div className="text-center py-8 text-slate-600">
                  Caricamento...
                </div>
              ) : sortedMonths.length === 0 ? (
                <div className="text-center py-8 text-slate-600">
                  {selectedStatus === 'completed' 
                    ? 'Nessun pagamento concluso'
                    : `Nessun pagamento ${selectedStatus === 'pending' ? 'in attesa' : 'aperto'}`
                  }
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Bottoni espandi/collassa tutti */}
                  {sortedMonths.length > 1 && (
                    <div className="flex gap-2 justify-end mb-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => toggleAllMonths(true)}
                        className="text-xs"
                      >
                        Espandi tutti
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => toggleAllMonths(false)}
                        className="text-xs"
                      >
                        Collassa tutti
                      </Button>
                    </div>
                  )}
                  
                  {sortedMonths.map((month) => {
                    const isExpanded = expandedMonths[month] ?? false;
                    const monthTotal = paymentsByMonth[month].reduce(
                      (sum, p) => sum + (selectedType === 'sdd' ? (p.sdd_amount || 0) : (p.sepa_amount || 0)),
                      0
                    );
                    
                    return (
                      <div key={month} className="border rounded-lg overflow-hidden">
                        {/* Header del mese - Cliccabile */}
                        <div 
                          className="flex items-center justify-between py-3 px-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => toggleMonth(month)}
                          data-testid={`month-header-${month}`}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-500" />
                            )}
                            <h3 className="font-semibold text-slate-900">
                              {formatMonth(month + '-01')}
                            </h3>
                            <Badge variant="secondary" className="ml-2">
                              {paymentsByMonth[month].length} pagamenti
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-slate-900">
                              {formatCurrency(monthTotal)}
                            </span>
                            {selectedStatus === 'completed' && month >= '2026-08' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-blue-500 hover:text-blue-700"
                                onClick={(e) => { e.stopPropagation(); handleImportaFatturePdfMese(month); }}
                                disabled={importingMonth === month}
                                title={`Importa da FIC i PDF ${selectedType.toUpperCase()} inviati allo SDI di ${formatMonth(month + '-01')}`}
                                data-testid={`importa-fic-mese-${month}`}
                              >
                                <FileText className="w-3.5 h-3.5 mr-1" />
                                {importingMonth === month ? 'Importo…' : `Import PDF ${selectedType.toUpperCase()}`}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-slate-500 hover:text-slate-700"
                              onClick={(e) => { e.stopPropagation(); handleExport('xlsx', month); }}
                              title={`Esporta ${formatMonth(month + '-01')} in XLSX`}
                              data-testid={`export-month-xlsx-${month}`}
                            >
                              <FileSpreadsheet className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-slate-500 hover:text-slate-700"
                              onClick={(e) => { e.stopPropagation(); handleExport('pdf', month); }}
                              title={`Esporta ${formatMonth(month + '-01')} in PDF`}
                              data-testid={`export-month-pdf-${month}`}
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Contenuto espandibile */}
                        {isExpanded && (
                          <div className="border-t">
                            <div className="max-h-[50vh] overflow-auto">
                              <Table>
                                <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                                  <TableRow>
                                    <TableHead className="w-10 text-center">#</TableHead>
                                    <TableHead className="w-12">
                                      <Checkbox
                                        checked={
                                          paymentsByMonth[month].length > 0 &&
                                          paymentsByMonth[month].every(p => selectedPayments.includes(p.id))
                                        }
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            const monthIds = paymentsByMonth[month].map(p => p.id);
                                            setSelectedPayments(prev => [...new Set([...prev, ...monthIds])]);
                                          } else {
                                            const monthIds = paymentsByMonth[month].map(p => p.id);
                                            setSelectedPayments(prev => prev.filter(id => !monthIds.includes(id)));
                                          }
                                        }}
                                        data-testid={`select-all-${month}`}
                                      />
                                    </TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Descrizione</TableHead>
                                    <TableHead>{selectedType === 'sdd' ? 'Chi Paga' : 'Beneficiario'}</TableHead>
                                    <TableHead className="text-right font-semibold">
                                      {selectedType === 'sdd' ? 'Totale SDD' : 'Totale SEPA'}
                                    </TableHead>
                                    <TableHead>Stato</TableHead>
                                    {/* Colonna Fattura per "Completati" */}
                                    {selectedStatus === 'completed' && (
                                      <TableHead>Fattura</TableHead>
                                    )}
                                    {/* Colonna Azioni solo per "Aperti" */}
                                    {selectedStatus === 'open' && (
                                      <TableHead className="text-center">Azioni</TableHead>
                                    )}
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {paymentsByMonth[month].map((payment, paymentIndex) => (
                            <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                              <TableCell className="text-center text-slate-400 font-mono text-sm">
                                {paymentIndex + 1}
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={selectedPayments.includes(payment.id)}
                                  onCheckedChange={(checked) => handleSelectPayment(payment.id, checked)}
                                  data-testid={`select-payment-${payment.id}`}
                                />
                              </TableCell>
                              <TableCell>
                                {payment.categoria === 'straordinario' ? (
                                  <div className="flex flex-col gap-1">
                                    <Badge className="bg-orange-100 text-orange-800 w-fit">Straordinario</Badge>
                                    {payment.metodo_pagamento === 'bonifico' && (
                                      <Badge className="bg-purple-100 text-purple-800 text-xs w-fit">
                                        <Banknote className="w-3 h-3 mr-1" />
                                        Bonifico
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <Badge variant="outline">Canone</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {payment.categoria === 'straordinario' && payment.straordinario_tipo !== 'prima_rata' ? (
                                  <div>
                                    <span className="text-sm">{payment.straordinario_descrizione || payment.straordinario_tipo}</span>
                                    {payment.note && (
                                      <p className="text-xs text-slate-500 mt-1">{payment.note}</p>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <span className="text-sm font-medium">
                                      {payment.contract_code || payment.contract_id?.substring(0, 8)}
                                    </span>
                                    <span className="text-slate-500 ml-2">
                                      {formatMonth(payment.month)}
                                    </span>
                                    {payment.rata_numero && payment.rata_totale && (
                                      <span className="text-xs text-slate-400 ml-2">
                                        (mensilità {payment.rata_numero}/{payment.rata_totale})
                                      </span>
                                    )}
                                    {payment.straordinario_tipo === 'prima_rata' && payment.note && (
                                      <p className="text-xs text-slate-500 mt-1">{payment.note}</p>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm">
                                  {selectedType === 'sdd' 
                                    ? (payment.pagatore_nome || 'N/A')
                                    : (payment.beneficiario_nome || 'N/A')}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {selectedType === 'sdd'
                                  ? formatCurrency(payment.sdd_amount)
                                  : formatCurrency(payment.sepa_amount)}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  {getStatusBadge(payment.status)}
                                  {payment.solleciti && payment.solleciti.length > 0 && (
                                    <Badge className="bg-amber-100 text-amber-800 text-xs w-fit">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      {payment.solleciti.length} solleciti
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              {/* Colonna Fattura per Completati */}
                              {selectedStatus === 'completed' && (
                                <TableCell>
                                  {(selectedType === 'sepa' && payment.categoria === 'straordinario' && payment.straordinario_tipo !== 'prima_rata') ? (
                                    <span
                                      className="inline-flex items-center rounded px-2 py-1 text-xs font-semibold bg-red-100 text-red-700 border border-red-200"
                                      data-testid={`no-fattura-${payment.id}`}
                                      title="Movimento senza fattura lato beneficiario: per gli straordinari la fattura si emette solo verso il pagatore (SDD)."
                                    >
                                      NO FATTURA
                                    </span>
                                  ) : (
                                  <div className="flex items-center gap-2">
                                    {payment.fattura_manuale ? (
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDownloadFatturaManuale(payment.id)}
                                          className="text-blue-600 text-xs h-7 px-2"
                                          title={payment.fattura_manuale.filename}
                                          data-testid={`download-fattura-manuale-${payment.id}`}
                                        >
                                          <Download className="w-3 h-3 mr-1" />
                                          {(payment.month || '').substring(0, 7) <= '2026-07' ? (
                                            <span>Fattura PDF</span>
                                          ) : (
                                            <span className="flex flex-col items-start leading-tight">
                                              <span>{payment.fattura_numero || (
                                                (payment.fattura_manuale.filename || '').length > 15 
                                                  ? payment.fattura_manuale.filename.substring(0, 15) + '...' 
                                                  : payment.fattura_manuale.filename
                                              )}</span>
                                              {payment.fattura_data && <span className="text-[9px] text-slate-400">{payment.fattura_data}</span>}
                                            </span>
                                          )}
                                        </Button>
                                      </div>
                                    ) : payment.fattura_numero ? (
                                      <div className="flex items-center gap-1">
                                        <div className="flex flex-col" data-testid={`fattura-numero-${payment.id}`}>
                                          <span className="text-sm font-mono text-slate-600">{payment.fattura_numero}</span>
                                          {payment.fattura_data && <span className="text-[9px] text-slate-400">{payment.fattura_data}</span>}
                                        </div>
                                        <label className="cursor-pointer" title="Carica PDF fattura">
                                          <input type="file" accept=".pdf" className="hidden"
                                            data-testid={`upload-fattura-inline-${payment.id}`}
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (!file) return;
                                              const formData = new FormData();
                                              formData.append('file', file);
                                              axios.post(`${API_URL}/payments/${payment.id}/fattura-manuale`, formData, {
                                                headers: { 'Content-Type': 'multipart/form-data' }
                                              }).then(() => {
                                                toast.success(`PDF "${file.name}" caricato`);
                                                fetchPayments();
                                              }).catch((err) => toast.error(err.response?.data?.detail || 'Errore upload'));
                                              e.target.value = '';
                                            }} />
                                          <Upload className="w-3 h-3 text-slate-400 hover:text-blue-600 transition-colors" />
                                        </label>
                                      </div>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleUploadFattura(payment.id)}
                                        className="text-xs h-7"
                                        data-testid={`upload-fattura-${payment.id}`}
                                      >
                                        <Upload className="w-3 h-3 mr-1" />
                                        Carica fattura
                                      </Button>
                                    )}
                                    {payment.fattura_fic_id && !payment.fattura_manuale && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleImportaFatturaFic(payment.id)}
                                        className="text-blue-500 hover:text-blue-700 h-7 px-2 text-xs"
                                        title="Importa PDF e stato SDI da Fatture in Cloud"
                                        data-testid={`importa-fic-${payment.id}`}
                                      >
                                        <FileText className="w-3 h-3 mr-1" />
                                        FIC
                                      </Button>
                                    )}
                                    {payment.ei_status && (
                                      <span className="text-[9px] px-1 rounded bg-slate-100 text-slate-600" title="Stato SDI" data-testid={`ei-status-${payment.id}`}>
                                        {payment.ei_status}
                                      </span>
                                    )}
                                  </div>
                                  )}
                                </TableCell>
                              )}
                              {/* Azioni per pagamenti "Aperti" */}
                              {selectedStatus === 'open' && (
                                <TableCell>
                                  <div className="flex gap-1 justify-center">
                                    {/* SDD: Conferma Incasso + Sollecita */}
                                    {selectedType === 'sdd' && (
                                      <>
                                        {/* Mensilità 1: Avviso Pagamento Bonifico */}
                                        {payment.rata_numero === 1 && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-8 px-3 text-purple-700 border-purple-300 hover:bg-purple-50"
                                            onClick={() => handleInviaAvvisoPagamento(payment)}
                                            data-testid={`btn-avviso-${payment.id}`}
                                            title="Invia Avviso Pagamento (prima mensilità = bonifico)"
                                          >
                                            <Mail className="w-4 h-4 mr-1" />
                                            Avviso Bonifico
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-8 px-3 text-green-700 border-green-300 hover:bg-green-50"
                                          onClick={() => handleConfermaSDD(payment.id)}
                                          data-testid={`btn-conferma-${payment.id}`}
                                          title="Conferma Incasso"
                                        >
                                          <Check className="w-4 h-4 mr-1" />
                                          Incasso
                                        </Button>
                                        {/* Sollecita solo per mensilità > 1 */}
                                        {payment.rata_numero !== 1 && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-8 px-3 text-amber-700 border-amber-300 hover:bg-amber-50"
                                            onClick={() => handleSollecita(payment.id)}
                                            data-testid={`btn-sollecita-${payment.id}`}
                                            title="Invia sollecito"
                                          >
                                            <Mail className="w-4 h-4 mr-1" />
                                            Sollecita
                                          </Button>
                                        )}
                                      </>
                                    )}
                                    {/* SEPA: Solo Conferma Pagamento */}
                                    {selectedType === 'sepa' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 px-3 text-blue-700 border-blue-300 hover:bg-blue-50"
                                        onClick={() => handleConfermaBonifico(payment.id)}
                                        data-testid={`btn-conferma-${payment.id}`}
                                        title="Conferma Pagamento"
                                      >
                                        <Check className="w-4 h-4 mr-1" />
                                        Pagamento
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Dialog per selezionare la data di esecuzione */}
      <Dialog open={showDataIncassoDialog} onOpenChange={setShowDataIncassoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {selectedType === 'sdd' ? 'Data di Incasso Distinta SDD' : 'Data Esecuzione Distinta Bonifici SEPA'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              {selectedType === 'sdd' 
                ? 'Seleziona la data in cui la banca dovrà eseguire l\'addebito sui conti dei debitori.'
                : 'Seleziona la data in cui la banca dovrà eseguire i bonifici ai beneficiari.'}
            </p>
            <div className="space-y-2">
              <Label htmlFor="data_incasso">
                {selectedType === 'sdd' ? 'Data Incasso (ReqdColltnDt)' : 'Data Esecuzione (ReqdExctnDt)'}
              </Label>
              <Input
                id="data_incasso"
                type="date"
                value={dataIncasso}
                onChange={(e) => setDataIncasso(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="data-incasso-input"
              />
              <p className="text-xs text-slate-500">
                Deve essere almeno 2-5 giorni lavorativi nel futuro (dipende dalla banca)
              </p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm font-medium">Riepilogo:</p>
              <p className="text-sm text-slate-600">
                • {selectedPayments.length} pagamenti selezionati<br />
                • Tipo: {selectedType === 'sdd' ? 'Addebito Diretto (SDD)' : 'Bonifico (SEPA)'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDataIncassoDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleConfirmGenerateDistinta} data-testid="confirm-generate-xml-btn">
              <Download className="w-4 h-4 mr-2" />
              Genera Distinta XML
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Errori Validazione Distinta */}
      <Dialog open={showValidationErrorsDialog} onOpenChange={setShowValidationErrorsDialog}>
        <DialogContent className="max-w-lg" aria-describedby="validation-errors-description">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Dati Mancanti per Distinta {selectedType === 'sdd' ? 'SDD' : 'Bonifici SEPA'}
            </DialogTitle>
            <DialogDescription id="validation-errors-description" className="sr-only">
              Elenco degli errori di validazione che impediscono la generazione della distinta
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Non è possibile generare la distinta perché mancano alcuni dati obbligatori. 
              Correggi i seguenti errori nella sezione <strong>Documenti Contratto</strong>:
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-60 overflow-y-auto">
              <ul className="space-y-2">
                {validationErrors.map((error, idx) => {
                  // Normalizza l'errore in stringa: può essere string OR oggetto Pydantic {loc, msg, type, ...}
                  let label;
                  if (typeof error === 'string') {
                    label = error;
                  } else if (error && typeof error === 'object') {
                    const loc = Array.isArray(error.loc) ? error.loc.filter(p => p !== 'body').join('.') : '';
                    label = loc ? `${loc}: ${error.msg || 'errore'}` : (error.msg || JSON.stringify(error));
                  } else {
                    label = String(error);
                  }
                  return (
                    <li key={`err-${idx}-${label.slice(0, 20)}`} className="text-sm text-red-700 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <p className="text-xs text-slate-500">
              {selectedType === 'sdd' 
                ? 'Vai in Contratti → Documenti per inserire i dati mancanti (ID Mandato SDD e Data Firma).'
                : 'Vai in Contratti → Documenti per inserire i dati mancanti (IBAN beneficiario).'}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowValidationErrorsDialog(false)}>
              Ho capito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Avviso Pagamento Bonifico */}
      <Dialog open={showAvvisoDialog} onOpenChange={setShowAvvisoDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Avviso Pagamento Bonifico
            </DialogTitle>
            <DialogDescription>
              Mensilità 1 — {avvisoPayment?.contract_code} — {avvisoPayment?.pagatore_nome}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium text-slate-700">Oggetto</Label>
              <Input
                value={avvisoOggetto}
                onChange={(e) => setAvvisoOggetto(e.target.value)}
                className="mt-1"
                data-testid="avviso-oggetto"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Messaggio</Label>
              <Textarea
                value={avvisoCorpo}
                onChange={(e) => setAvvisoCorpo(e.target.value)}
                className="mt-1 min-h-[200px] font-mono text-sm"
                data-testid="avviso-corpo"
              />
            </div>
            <p className="text-xs text-slate-500">
              Modifica il template da <strong>Template Testi → Avviso Pagamento Bonifico</strong>
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAvvisoDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleConfirmAvviso}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="avviso-confirm-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Prepara Avviso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Fattura */}
      <Dialog open={showFatturaDialog} onOpenChange={setShowFatturaDialog}>
        <DialogContent className="max-w-sm" data-testid="fattura-dialog">
          <DialogHeader>
            <DialogTitle>Conferma e Fatturazione</DialogTitle>
            <DialogDescription>
              Vuoi generare la fattura su Fatture in Cloud?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Data Fattura</Label>
              <Input
                type="date"
                value={dataFattura}
                onChange={(e) => setDataFattura(e.target.value)}
                min={(() => { const d = new Date(); d.setDate(d.getDate() - 10); return d.toISOString().split('T')[0]; })()}
                max={new Date().toISOString().split('T')[0]}
                className="mt-1 w-full [&::-webkit-calendar-picker-indicator]:opacity-50"
                data-testid="data-fattura-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              La fattura verrà creata come bozza su Fatture in Cloud.<br />Non verrà inviata allo SDI.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => handleConfermaConFattura(false)} data-testid="conferma-senza-fattura-btn" className="flex-1">
              Senza fattura
            </Button>
            <Button onClick={() => handleConfermaConFattura(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="conferma-con-fattura-btn">
              <FileText className="w-4 h-4 mr-1" />
              Genera Fattura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avanzamento fatturazione collettiva sequenziale */}
      <Dialog open={showProgressDialog} onOpenChange={(o) => { if (!o && fatturazioneJob && fatturazioneJob.status === 'running') return; setShowProgressDialog(o); }}>
        <DialogContent className="max-w-lg" data-testid="fatturazione-progress-dialog">
          <DialogHeader>
            <DialogTitle>Emissione fatture in corso</DialogTitle>
            <DialogDescription>
              Le fatture vengono emesse una alla volta e verificate su Fatture in Cloud.
            </DialogDescription>
          </DialogHeader>
          {fatturazioneJob && (() => {
            const j = fatturazioneJob;
            const tot = j.totale || 0;
            const done = j.elaborate || 0;
            const perc = tot > 0 ? Math.round((done / tot) * 100) : 0;
            const finito = j.status === 'completed' || j.status === 'error';
            return (
              <div className="space-y-4 py-2">
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium" data-testid="fatturazione-progress-label">
                      {finito ? 'Completato' : `Fattura ${Math.min(done + 1, tot)} di ${tot}`}
                      {!finito && j.corrente_label ? ` — ${j.corrente_label}` : ''}
                    </span>
                    <span className="text-slate-500">{done}/{tot} ({perc}%)</span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${j.status === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}
                      style={{ width: `${perc}%` }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="bg-emerald-50 rounded p-2" data-testid="fatturazione-emesse">
                    <div className="text-lg font-bold text-emerald-700">{j.emesse || 0}</div>
                    <div className="text-xs text-slate-500">Emesse</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2" data-testid="fatturazione-saltate">
                    <div className="text-lg font-bold text-slate-600">{j.saltate || 0}</div>
                    <div className="text-xs text-slate-500">Saltate</div>
                  </div>
                  <div className="bg-red-50 rounded p-2" data-testid="fatturazione-errori">
                    <div className="text-lg font-bold text-red-600">{j.errori || 0}</div>
                    <div className="text-xs text-slate-500">Errori</div>
                  </div>
                </div>
                {Array.isArray(j.dettagli) && j.dettagli.length > 0 && (
                  <div className="max-h-52 overflow-y-auto border rounded divide-y">
                    {j.dettagli.slice().reverse().map((d, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                        <span className="font-mono">{d.etichetta || d.payment_id?.substring(0, 8)}</span>
                        {d.ok && d.skipped ? (
                          <span className="text-slate-500">Saltata ({d.reason || 'n/d'})</span>
                        ) : d.ok ? (
                          <span className="text-emerald-700">
                            {d.numero} {d.verificata ? `✓ FiC${d.ei_status ? ' (' + d.ei_status + ')' : ''}` : '· non verificata'}
                          </span>
                        ) : (
                          <span className="text-red-600 truncate max-w-[220px]" title={d.error}>Errore: {d.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!finito && (
                  <p className="text-xs text-slate-500 text-center">Non chiudere questa finestra: l'operazione è in corso…</p>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button
              variant={fatturazioneJob && (fatturazioneJob.status === 'completed' || fatturazioneJob.status === 'error') ? 'default' : 'outline'}
              disabled={fatturazioneJob && fatturazioneJob.status === 'running'}
              onClick={() => setShowProgressDialog(false)}
              data-testid="fatturazione-close-btn"
              className="w-full"
            >
              {fatturazioneJob && fatturazioneJob.status === 'running' ? 'Attendere…' : 'Chiudi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Hidden file input for manual invoice upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx"
        className="hidden"
        data-testid="fattura-file-input"
      />

      {/* Modale Verifica Coerenza — riconciliazione pending vs contratto */}
      <Dialog open={showCoerenzaDialog} onOpenChange={setShowCoerenzaDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="coerenza-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Verifica Coerenza Pagamenti Pending
            </DialogTitle>
            <DialogDescription>
              Controlla che gli importi dei pagamenti in stato &quot;In Attesa&quot; siano allineati ai dati economici correnti dei contratti (canone, spese, sconti). Nessun pagamento &quot;Aperto&quot; o &quot;Completato&quot; viene toccato.
            </DialogDescription>
          </DialogHeader>

          {coerenzaLoading && (
            <div className="flex items-center gap-3 py-8 justify-center text-slate-500">
              <RefreshCw className="w-5 h-5 animate-spin" />
              Analisi in corso…
            </div>
          )}

          {!coerenzaLoading && coerenzaData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                  <div className="text-xs text-slate-500">Contratti controllati</div>
                  <div className="text-2xl font-bold text-slate-900" data-testid="coerenza-count-contratti">
                    {coerenzaData.contratti_controllati || 0}
                  </div>
                </div>
                <div className={`rounded-lg border p-3 ${
                  (coerenzaData.disallineamenti_totali || 0) === 0
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-amber-200 bg-amber-50'
                }`}>
                  <div className="text-xs text-slate-500">Disallineamenti</div>
                  <div className={`text-2xl font-bold ${
                    (coerenzaData.disallineamenti_totali || 0) === 0
                      ? 'text-emerald-700'
                      : 'text-amber-700'
                  }`} data-testid="coerenza-count-disallineamenti">
                    {coerenzaData.disallineamenti_totali || 0}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                  <div className="text-xs text-slate-500">Corretti in questa sessione</div>
                  <div className="text-2xl font-bold text-slate-900" data-testid="coerenza-count-aggiornati">
                    {coerenzaData.aggiornati || 0}
                  </div>
                </div>
              </div>

              {(coerenzaData.disallineamenti_totali || 0) === 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800" data-testid="coerenza-ok-banner">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Tutti i pagamenti pending sono coerenti con i dati economici dei contratti. Puoi procedere all&apos;invio distinta in sicurezza.</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800 text-sm">
                    <AlertTriangle className="w-5 h-5 shrink-0" />
                    <span>Rilevati disallineamenti tra pagamenti &quot;In Attesa&quot; e dati economici correnti del contratto. Applica le correzioni per riallineare gli importi.</span>
                  </div>

                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contratto</TableHead>
                          <TableHead className="text-center">Rata</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="text-right">Attuale</TableHead>
                          <TableHead className="text-right">Atteso</TableHead>
                          <TableHead className="text-right">Δ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(coerenzaData.dettagli || []).map((d, idx) => {
                          const currentAmt = d.tipo === 'sdd' ? d.current?.sdd : d.current?.sepa;
                          const expectedAmt = d.tipo === 'sdd' ? d.expected?.sdd : d.expected?.sepa;
                          const delta = (expectedAmt || 0) - (currentAmt || 0);
                          return (
                            <TableRow key={`${d.payment_id || idx}`} data-testid={`coerenza-row-${idx}`}>
                              <TableCell className="font-mono text-xs">{d.codice_contratto}</TableCell>
                              <TableCell className="text-center">{d.rata_numero}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="uppercase">{d.tipo}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {currentAmt != null ? `€ ${Number(currentAmt).toFixed(2)}` : '—'}
                              </TableCell>
                              <TableCell className="text-right font-mono text-emerald-700 font-semibold">
                                {expectedAmt != null ? `€ ${Number(expectedAmt).toFixed(2)}` : '—'}
                              </TableCell>
                              <TableCell className={`text-right font-mono ${
                                delta === 0 ? 'text-slate-400' : (delta > 0 ? 'text-emerald-600' : 'text-red-600')
                              }`}>
                                {delta === 0 ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  {coerenzaData.dettagli && coerenzaData.dettagli.length >= 100 && (
                    <p className="text-xs text-slate-500 italic">Mostrati primi 100 disallineamenti. Applica le correzioni per riallineare tutti i pagamenti.</p>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCoerenzaDialog(false)}
              data-testid="coerenza-close-btn"
            >
              Chiudi
            </Button>
            {!coerenzaLoading && coerenzaData && (coerenzaData.disallineamenti_totali || 0) > 0 && (
              <Button
                onClick={handleApplicaCorrezioni}
                disabled={coerenzaApplying}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="coerenza-apply-btn"
              >
                {coerenzaApplying ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Applicazione…</>
                ) : (
                  <><ShieldCheck className="w-4 h-4 mr-2" />Applica Correzioni</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagamentiPage;
